import React from "react";
import magic from "./magic";
import can from "./stack/can";
import canopen from "./stack/canopen";
import "./Dashboard.css";
import SdoTool from "./components/SdoTool";
import FirmwareFlasher from "./components/FirmwareFlasher";
import NodesPage from "./components/NodesPage";

// Admin dashboard with multiple pages
export default function Dashboard() {
  const [status, setStatus] = React.useState("disconnected");
  const [wsStatus, setWsStatus] = React.useState("disconnected");
  const [wsUrl, setWsUrl] = React.useState("ws://twisted-pair:8080");
  const [transport, setTransport] = React.useState("ble"); // 'ble' | 'ws'
  const [activePage, setActivePage] = React.useState("connect"); // 'connect' | 'log' | 'byId' | 'canopen' | 'sdo' | 'nodes'

  // CAN stream state
  const [frames, setFrames] = React.useState([]);
  const [framesLimit, setFramesLimit] = React.useState(500); // default 500
  const framesLimitRef = React.useRef(framesLimit);
  const [autoscroll, setAutoscroll] = React.useState(true);
  const [logCapturing, setLogCapturing] = React.useState(true);
  const logCapturingRef = React.useRef(logCapturing);

  // Latest-by-ID (for ID pages)
  const [byId, setById] = React.useState(() => new Map());

  const listRef = React.useRef(null);
  const [nowTs, setNowTs] = React.useState(Date.now());

  React.useEffect(() => {
    // Initialize CANopen layer
    canopen.init();
    
    const onStatus = (s) => setStatus(s);
    const onWsStatus = (s) => setWsStatus(s);
    const onRaw = (frame) => {
      // Always update latest-by-ID
      setById((prev) => {
        const next = new Map(prev);
        const last = prev.get(frame.id);
        const deltaMs = last ? frame.timestamp - last.timestamp : null;
        next.set(frame.id, { ...frame, deltaMs });
        return next;
      });
      // Append to stream if capturing
      if (logCapturingRef.current) {
        setFrames((prev) => {
          const next = [...prev, frame];
          const limit = framesLimitRef.current;
          const over = next.length - (typeof limit === 'number' ? limit : 500);
          if (over > 0) next.splice(0, over);
          return next;
        });
      }
    };

    can.eventEmitter.on("status", onStatus);
    can.eventEmitter.on("ws-status", onWsStatus);
    can.eventEmitter.on("raw", onRaw);
    return () => {
      can.eventEmitter.removeListener("status", onStatus);
      can.eventEmitter.removeListener("ws-status", onWsStatus);
      can.eventEmitter.removeListener("raw", onRaw);
      // Clean up CANopen layer
      canopen.cleanup();
    };
  }, []);

  React.useEffect(() => {
    if (!autoscroll || activePage !== "log") return;
    const el = listRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [frames, autoscroll, activePage]);

  // Trim frames if limit reduced
  React.useEffect(() => {
    setFrames((prev) => (prev.length > framesLimit ? prev.slice(prev.length - framesLimit) : prev));
  }, [framesLimit]);

  // Keep refs in sync so onRaw sees latest settings
  React.useEffect(() => {
    framesLimitRef.current = framesLimit;
  }, [framesLimit]);
  React.useEffect(() => {
    logCapturingRef.current = logCapturing;
  }, [logCapturing]);

  // Ticker for fading rows based on age in ID views
  React.useEffect(() => {
    if (activePage !== "byId" && activePage !== "canopen") return;
    const h = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(h);
  }, [activePage]);

  const formatDelta = (ms) => {
    if (ms == null) return "—";
    if (ms < 1000) return `${ms} ms`;
    if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${m}m ${s}s`;
  };

  const rowOpacityForAge = (ageMs) => {
    // Fade from 1 at 0s to 0.4 at 30s+, clamp range
    const t = Math.min(1, Math.max(0, ageMs / 30000));
    return 1 - t * 0.6; // 1 -> 0.4
  };

  // Export helpers
  const handleExportStream = () => {
    const lines = frames.map((f) => {
      const ts = new Date(f.timestamp).toISOString();
      const idHex = "0x" + f.id.toString(16).toUpperCase();
      const payloadHex = Array.from(f.payload)
        .map((b) => b.toString(16).padStart(2, "0").toUpperCase())
        .join(" ");
      return `${ts} ${idHex} [${f.payload.length}] ${payloadHex}`;
    });
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `can-log-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportById = () => {
    const rows = Array.from(byId.entries()).sort((a, b) => a[0] - b[0]);
    const lines = rows.map(([id, f]) => {
      const ts = new Date(f.timestamp).toISOString();
      const idHex = "0x" + id.toString(16).toUpperCase();
      const payloadHex = Array.from(f.payload)
        .map((b) => b.toString(16).padStart(2, "0").toUpperCase())
        .join(" ");
      return `${ts} ${idHex} [${f.payload.length}] ${payloadHex}`;
    });
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `can-byid-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Basic CANopen ID decoding (PDOs, SDO, etc.)
  const decodeCANopen = (id) => {
    const add = (type, nodeId, details) => ({ type, nodeId, details });
    const inRange = (x, a, b) => x >= a && x <= b;
    if (id === 0x000) return add("NMT", null, "Network Management");
    if (id === 0x080) return add("SYNC", null, "Synchronization");
    if (id === 0x100) return add("TIME", null, "Time Stamp");
    if (inRange(id, 0x180, 0x1ff)) return add("TPDO1", id - 0x180, "PDO TX #1");
    if (inRange(id, 0x200, 0x27f)) return add("RPDO1", id - 0x200, "PDO RX #1");
    if (inRange(id, 0x280, 0x2ff)) return add("TPDO2", id - 0x280, "PDO TX #2");
    if (inRange(id, 0x300, 0x37f)) return add("RPDO2", id - 0x300, "PDO RX #2");
    if (inRange(id, 0x380, 0x3ff)) return add("TPDO3", id - 0x380, "PDO TX #3");
    if (inRange(id, 0x400, 0x47f)) return add("RPDO3", id - 0x400, "PDO RX #3");
    if (inRange(id, 0x480, 0x4ff)) return add("TPDO4", id - 0x480, "PDO TX #4");
    if (inRange(id, 0x500, 0x57f)) return add("RPDO4", id - 0x500, "PDO RX #4");
    if (inRange(id, 0x580, 0x5ff)) return add("SDO-TX", id - 0x580, "SDO Server -> Client");
    if (inRange(id, 0x600, 0x67f)) return add("SDO-RX", id - 0x600, "SDO Client -> Server");
    if (inRange(id, 0x700, 0x77f)) return add("HEARTBEAT", id - 0x700, "Node Heartbeat");
    return add("Other", null, "");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      {/* Top Navbar */}
      <header className="dash-navbar">
        <div className="dash-brand">Smart Sprinter Admin</div>
        <nav className="dash-tabs" role="navigation" aria-label="Dashboard">
          <button className={activePage === "connect" ? "active" : ""} onClick={() => setActivePage("connect")}>Connect</button>
          <button className={activePage === "log" ? "active" : ""} onClick={() => setActivePage("log")}>CAN Log</button>
          <button className={activePage === "byId" ? "active" : ""} onClick={() => setActivePage("byId")}>CAN ID</button>
          <button className={activePage === "canopen" ? "active" : ""} onClick={() => setActivePage("canopen")}>CANopen ID</button>
          <button className={activePage === "nodes" ? "active" : ""} onClick={() => setActivePage("nodes")}>Nodes</button>
          <button className={activePage === "sdo" ? "active" : ""} onClick={() => setActivePage("sdo")}>SDO</button>
        </nav>
        <div className="dash-status">
          <span className={`status-dot ${status === "connected" ? "ok" : "off"}`} title={`BLE: ${status}`} />
          <span style={{ marginRight: 12 }}>BLE: {String(status)}</span>
          <span className={`status-dot ${wsStatus === "connected" ? "ok" : "off"}`} title={`WS: ${wsStatus}`} />
          <span>WS: {String(wsStatus)}</span>
        </div>
      </header>

      {/* Pages */}
      <main style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {activePage === "connect" && (
          <section style={{ padding: 16 }}>
            <h2>Connection</h2>
            <p>Select a transport to receive CAN frames: BLE or WebSocket.</p>
            <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="radio" name="transport" value="ble" checked={transport === "ble"} onChange={() => setTransport("ble")} /> BLE
              </label>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="radio" name="transport" value="ws" checked={transport === "ws"} onChange={() => setTransport("ws")} /> WebSocket
              </label>
            </div>

            {transport === "ble" && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                <button onClick={() => can.connectBLE()} disabled={status === "connected"}>Connect BLE</button>
                <button onClick={() => can.disconnectBLE()} disabled={status === "disconnected"}>Disconnect</button>
                <span style={{ marginLeft: 8, opacity: 0.8 }}>Status: {String(status)}</span>
              </div>
            )}

            {transport === "ws" && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
                <input type="text" value={wsUrl} onChange={(e) => setWsUrl(e.target.value)} placeholder="ws://host:port" style={{ width: 280 }} />
                <button onClick={() => can.connectWS(wsUrl)} disabled={wsStatus === "connected"}>Connect WS</button>
                <button onClick={() => can.disconnectWS()} disabled={wsStatus === "disconnected"}>Disconnect</button>
                <span style={{ marginLeft: 8, opacity: 0.8 }}>WS: {String(wsStatus)}</span>
              </div>
            )}
          </section>
        )}

        {activePage === "log" && (
          <section style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
            <div style={{ padding: 12, borderBottom: "1px solid #e5e7eb", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <strong>Raw CAN Frames</strong> — {frames.length} shown
              <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                Line limit
                <input type="number" min={1} step={100} value={framesLimit} onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!Number.isNaN(val)) setFramesLimit(Math.max(1, Math.min(200000, val)));
                }} style={{ width: 100 }} />
              </label>
              <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input type="checkbox" checked={autoscroll} onChange={(e) => setAutoscroll(e.target.checked)} /> Autoscroll
              </label>
              <button onClick={() => setLogCapturing((p) => !p)}>{logCapturing ? "Stop" : "Start"}</button>
              <button onClick={() => setFrames([])}>Clear</button>
              <button onClick={handleExportStream} disabled={!frames.length}>Export .txt</button>
            </div>
            <div ref={listRef} style={{
              flex: 1, overflow: "auto", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace",
              fontSize: 12, padding: 12, background: "#0b1020", color: "#e4e7f5"
            }}>
              {frames.map((f, idx) => {
                const ts = new Date(f.timestamp).toLocaleTimeString();
                const idHex = f.id.toString(16).toUpperCase().padStart(3, "0");
                const payloadHex = Array.from(f.payload).map((b) => b.toString(16).padStart(2, "0").toUpperCase()).join(" ");
                return <div key={idx} style={{ whiteSpace: "pre" }}>{ts}  ID:{idHex}  LEN:{f.payload.length}  {payloadHex}</div>;
              })}
              {!frames.length && <div style={{ opacity: 0.6 }}>No frames yet. Connect via BLE or WebSocket to start streaming.</div>}
            </div>
          </section>
        )}

        {activePage === "byId" && (
          <section style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
            <div style={{ padding: 12, borderBottom: "1px solid #e5e7eb", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <strong>Latest by CAN ID</strong> — {byId.size} IDs
              <button onClick={handleExportById} disabled={byId.size === 0}>Export .txt</button>
            </div>
            <div style={{ flex: 1, overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #e5e7eb" }}>ID</th>
                    <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #e5e7eb" }}>Len</th>
                    <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #e5e7eb" }}>Payload</th>
                    <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #e5e7eb" }}>Δ</th>
                    <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #e5e7eb" }}>Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from(byId.entries()).sort((a, b) => a[0] - b[0]).map(([id, f]) => {
                    const idHex = id.toString(16).toUpperCase().padStart(3, "0");
                    const payloadHex = Array.from(f.payload).map((b) => b.toString(16).padStart(2, "0").toUpperCase()).join(" ");
                    const last = new Date(f.timestamp).toLocaleTimeString();
                    const age = nowTs - f.timestamp;
                    const opacity = rowOpacityForAge(age);
                    return (
                      <tr key={id} style={{ opacity }} title={`Last: ${new Date(f.timestamp).toISOString()}  Δ: ${formatDelta(f.deltaMs)}`}>
                        <td style={{ padding: 8, borderBottom: "1px solid #f0f0f0", fontFamily: "monospace" }}>0x{idHex}</td>
                        <td style={{ padding: 8, borderBottom: "1px solid #f0f0f0" }}>{f.payload.length}</td>
                        <td style={{ padding: 8, borderBottom: "1px solid #f0f0f0", fontFamily: "monospace" }}>{payloadHex}</td>
                        <td style={{ padding: 8, borderBottom: "1px solid #f0f0f0" }}>{formatDelta(f.deltaMs)}</td>
                        <td style={{ padding: 8, borderBottom: "1px solid #f0f0f0" }}>{last}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!byId.size && <div style={{ opacity: 0.6, padding: 12 }}>No frames yet. Connect via BLE or WebSocket to start streaming.</div>}
            </div>
          </section>
        )}

        {activePage === "canopen" && (
          <section style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
            <div style={{ padding: 12, borderBottom: "1px solid #e5e7eb", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <strong>CANopen — Latest by ID</strong> — {byId.size} IDs
            </div>
            <div style={{ flex: 1, overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #e5e7eb" }}>Object</th>
                    <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #e5e7eb" }}>Len</th>
                    <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #e5e7eb" }}>Payload</th>
                    <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #e5e7eb" }}>Δ</th>
                    <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #e5e7eb" }}>Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const entries = Array.from(byId.entries()).sort((a, b) => a[0] - b[0]);
                    const groups = new Map();
                    for (const [id, f] of entries) {
                      const info = decodeCANopen(id);
                      const key = info.nodeId == null ? -1 : info.nodeId;
                      if (!groups.has(key)) groups.set(key, []);
                      groups.get(key).push({ id, f, info });
                    }
                    return Array.from(groups.entries())
                      .sort((a, b) => a[0] - b[0])
                      .flatMap(([nodeKey, rows]) => {
                        const header = (
                          <tr className="group-row" key={`g-${nodeKey}`}>
                            <td colSpan={5} style={{ padding: 8 }}>
                              {nodeKey === -1 ? "System / Global" : `Node ${nodeKey}`}
                            </td>
                          </tr>
                        );
                        const body = rows
                          .sort((a, b) => a.id - b.id)
                          .map(({ id, f, info }) => {
                            const payloadHex = Array.from(f.payload)
                              .map((b) => b.toString(16).padStart(2, "0").toUpperCase())
                              .join(" ");
                            const last = new Date(f.timestamp).toLocaleTimeString();
                            const age = nowTs - f.timestamp;
                            const opacity = rowOpacityForAge(age);
                            return (
                              <tr key={id} style={{ opacity }} title={`Last: ${new Date(f.timestamp).toISOString()}  Δ: ${formatDelta(f.deltaMs)}`}>
                                <td style={{ padding: 8, borderBottom: "1px solid #f0f0f0" }}>
                                  {info.details || info.type}
                                </td>
                                <td style={{ padding: 8, borderBottom: "1px solid #f0f0f0" }}>{f.payload.length}</td>
                                <td style={{ padding: 8, borderBottom: "1px solid #f0f0f0", fontFamily: "monospace" }}>{payloadHex}</td>
                                <td style={{ padding: 8, borderBottom: "1px solid #f0f0f0" }}>{formatDelta(f.deltaMs)}</td>
                                <td style={{ padding: 8, borderBottom: "1px solid #f0f0f0" }}>{last}</td>
                              </tr>
                            );
                          });
                        return [header, ...body];
                      });
                  })()}
                </tbody>
              </table>
              {!byId.size && <div style={{ opacity: 0.6, padding: 12 }}>No frames yet. Connect via BLE or WebSocket to start streaming.</div>}
            </div>
          </section>
        )}

        {activePage === "sdo" && (
          <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1, overflowY: "auto" }}>
            <SdoTool />
            <FirmwareFlasher />
          </div>
        )}

        {activePage === "nodes" && (
          <NodesPage />
        )}
      </main>
    </div>
  );
}
